import React from "react";
import dayjs from "dayjs";
import { Page, Text, View, Document, StyleSheet, Image, pdf } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    padding: 32,
    lineHeight: 1.6,
  },
  image: {
    height: 24,
  },
  heading: {
    fontSize: 16,
    marginBottom: 8,
    fontWeight: "bold",
    color: "#333",
  },
  subheading: {
    fontSize: 14,
    marginTop: 12,
    marginBottom: 8,
    fontWeight: "bold",
    color: "#333",
  },
  line: {
    height: 1,
    backgroundColor: "grey",
    width: "100%",
    marginVertical: 8,
  },
  field: {
    fontWeight: "bold",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  column: {
    width: "48%",
  },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    borderBottom: "1px solid #ccc",
    paddingBottom: 4,
    marginBottom: 6,
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 4,
  },
  cell: {
    width: "34%",
  },
  cellWide: {
    width: "32%",
  },
});

const normalizeFieldTypeLabel = (typeValue) => {
  const raw = String(typeValue || "").trim().toLowerCase();
  if (raw === "upload-field" || raw === "upload") return "Upload Field";
  if (raw === "text-field" || raw === "text") return "Text Field";
  return raw || "Unknown";
};

const IVRInfoPdf = ({ ivrData, ivrProduct, infoFields }) => {
  const submissionDate = ivrData?.SubmissionDate
    ? dayjs(ivrData.SubmissionDate).format("MM-DD-YYYY")
    : "N/A";

  const fields = Array.isArray(infoFields) ? infoFields : [];

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.row}>
          <Text style={styles.heading}>IVR Info Required</Text>
          <Image
            style={styles.image}
            src={`${process.env.PUBLIC_URL}/companyLogo.png`}
          />
        </View>
        <View style={styles.line} />
        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>IVRAS ID:</Text> {ivrData?.IVRASID || "N/A"}
              </Text>
              <Text>
                <Text style={styles.field}>Account Name:</Text> {ivrData?.AccountName || "N/A"}
              </Text>
            </View>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Submission Date:</Text> {submissionDate}
              </Text>
              <Text>
                <Text style={styles.field}>Insurance Status:</Text> {ivrProduct?.InsuranceStatus || "N/A"}
              </Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Product Group:</Text>{" "}
                {ivrProduct?.ProductGroupName || "N/A"}
              </Text>
            </View>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Case ID:</Text> {ivrProduct?.CaseID || "N/A"}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.line} />
        <Text style={styles.subheading}>Requested Information</Text>
        {fields.length === 0 ? (
          <Text>No info fields were configured for this IVR product.</Text>
        ) : (
          <>
            <View style={styles.tableHeader}>
              <Text style={styles.cell}>Field Name</Text>
              <Text style={styles.cell}>Field Type</Text>
              <Text style={styles.cellWide}>Value</Text>
            </View>
            {fields.map((field, index) => {
              const fieldTypeLabel = normalizeFieldTypeLabel(
                field?.FieldType?.value ?? field?.FieldType
              );
              const value =
                fieldTypeLabel === "Upload Field"
                  ? field?.FieldValueFileName || "Not provided"
                  : field?.FieldValueText || "Not provided";
              return (
                <View key={field?.ID ?? index} style={styles.tableRow}>
                  <Text style={styles.cell}>{field?.FieldName || "N/A"}</Text>
                  <Text style={styles.cell}>{fieldTypeLabel}</Text>
                  <Text style={styles.cellWide}>{value}</Text>
                </View>
              );
            })}
          </>
        )}
      </Page>
    </Document>
  );
};

const createIVRInfoPdfBlob = async ({ ivrData, ivrProduct, infoFields }) => {
  return pdf(
    <IVRInfoPdf ivrData={ivrData} ivrProduct={ivrProduct} infoFields={infoFields} />
  ).toBlob();
};

export { IVRInfoPdf, createIVRInfoPdfBlob };
